import { createAction, props } from '@ngrx/store';
import {
  OnboardingStepData,
  ExtractionStatus,
  ProductExtractionData,
  OnboardingLead,
  OnboardingCompleteResult,
  OnboardingTargetingData
} from './onboarding.state';

// ============================================
// Session Init Actions
// ============================================
export const initOnboardingSession = createAction(
  '[Onboarding] Init Session'
);

export const initOnboardingSessionSuccess = createAction(
  '[Onboarding] Init Session Success',
  props<{
    sessionId: string;
    currentStep: number;
    expiresAt: string;
  }>()
);

export const initOnboardingSessionFailure = createAction(
  '[Onboarding] Init Session Failure',
  props<{ error: any }>()
);

// ============================================
// Session Resume Actions
// ============================================
export const resumeOnboardingSession = createAction(
  '[Onboarding] Resume Session',
  props<{ sessionId: string }>()
);

export const resumeOnboardingSessionSuccess = createAction(
  '[Onboarding] Resume Session Success',
  props<{
    sessionId: string;
    currentStep: number;
    expiresAt: string;
    stepData: OnboardingStepData;
    extractionStatus?: ExtractionStatus;
    productDescription?: string;
    companyName?: string;
    benefits?: string[];
    pains?: string[];
  }>()
);

export const resumeOnboardingSessionFailure = createAction(
  '[Onboarding] Resume Session Failure',
  props<{ error: any }>()
);

// ============================================
// Update Step Actions
// ============================================
export const updateOnboardingStep = createAction(
  '[Onboarding] Update Step',
  props<{ step: number; data: any }>()
);

export const updateOnboardingStepSuccess = createAction(
  '[Onboarding] Update Step Success',
  props<{ step: number; data: any; stepData: OnboardingStepData }>()
);

export const updateOnboardingStepFailure = createAction(
  '[Onboarding] Update Step Failure',
  props<{ error: any }>()
);

export const resetUpdateStepState = createAction(
  '[Onboarding] Reset Update Step State'
);

// ============================================
// Extract Product Actions
// ============================================
export const extractProduct = createAction(
  '[Onboarding] Extract Product',
  props<{ websiteUrl: string; companyName?: string }>()
);

export const extractProductSuccess = createAction(
  '[Onboarding] Extract Product Success',
  props<{ extractionStatus: ExtractionStatus; message: string }>()
);

export const extractProductFailure = createAction(
  '[Onboarding] Extract Product Failure',
  props<{ error: any }>()
);

// ============================================
// Poll Extraction Status Actions
// ============================================
export const pollExtractionStatus = createAction(
  '[Onboarding] Poll Extraction Status'
);

export const pollExtractionStatusSuccess = createAction(
  '[Onboarding] Poll Extraction Status Success',
  props<{
    extractionStatus: ExtractionStatus;
    extractionSource?: string;
    productDescription?: string;
    companyName?: string;
    benefits?: string[];
    pains?: string[];
  }>()
);

export const pollExtractionStatusFailure = createAction(
  '[Onboarding] Poll Extraction Status Failure',
  props<{ error: any }>()
);

export const stopPollingExtractionStatus = createAction(
  '[Onboarding] Stop Polling Extraction Status'
);

export const extractionStillProcessing = createAction(
  '[Onboarding] Extraction Still Processing',
  props<{ extractionStatus: ExtractionStatus }>()
);

export const extractionTimeout = createAction(
  '[Onboarding] Extraction Timeout'
);

// ============================================
// Complete Onboarding Actions
// ============================================
// Step 2 targeting travels with the completion request itself. Dispatching a separate
// updateOnboardingStep first raced this call, so the backend regularly read back null
// targeting and lead generation failed with "No targeting filters available".
export const completeOnboarding = createAction(
  '[Onboarding] Complete',
  props<{ data?: OnboardingTargetingData }>()
);

export const completeOnboardingSuccess = createAction(
  '[Onboarding] Complete Success',
  props<{ result: OnboardingCompleteResult }>()
);

export const completeOnboardingFailure = createAction(
  '[Onboarding] Complete Failure',
  props<{ error: any }>()
);

// ============================================
// WebSocket Lead Events
// ============================================
export const startListeningForLeads = createAction(
  '[Onboarding] Start Listening For Leads',
  props<{ userId: number }>()
);

export const stopListeningForLeads = createAction(
  '[Onboarding] Stop Listening For Leads'
);

export const leadGenerated = createAction(
  '[Onboarding] Lead Generated',
  props<{ lead: OnboardingLead; generationStatus?: string | null }>()
);

// ============================================
// Local State Management Actions
// ============================================
export const setCurrentStep = createAction(
  '[Onboarding] Set Current Step',
  props<{ step: number }>()
);

export const setStepData = createAction(
  '[Onboarding] Set Step Data',
  props<{ stepKey: string; data: any }>()
);

export const resetOnboardingState = createAction(
  '[Onboarding] Reset State'
);
